import useGTM from '@elgorditosalsero/react-gtm-hook'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import defaultAxios from 'axios'
import { useTranslation } from 'gatsby-plugin-react-i18next'
import { Close } from '@mui/icons-material'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useLocation from 'react-use/lib/useLocation'
import useAxios, { defaultRetryConfig } from '../../../hooks/useAxios'
import { useComplianceNote } from '../../../hooks/useComplianceNote'
import useLeadActions from '../../../hooks/useLeadActions'
import useSiteMetadata from '../../../hooks/useSiteMetadata'
import { EventNames, useStatsEvent } from '../../../hooks/useStatsEvent'
import useUser from '../../../hooks/useUser'
import LockIcon from '../../../images/gray-lock.inline.svg'
import { Endpoints, Timeouts } from '../../../types/constants'
import {
  LeadsEmailPayload,
  PartnerApiResponse,
  UploadGTMEvents,
  WidgetTRefs,
} from '../../../types/widget'
import { useCommonStyles } from '../../../utils/styles'
import { isEmail } from '../../../utils/validation'
import {
  isUploadSucess,
  useWidgetQueries,
  widgetSessionUtils,
} from '../../../utils/widget'
import ComplianceNote from '../../ComplianceNote/ComplianceNote'
import BlueDotLoader from '../../Loaders/BlueDotLoader'
import ResumeUploadStatus from '../../Modals/ResumeUploadStatus'
import ResumeUploadStatusV2 from '../../Modals/ResumeUploadStatusV2'
import { useBETelemetry } from '@talentinc/gatsby-theme-ecom/hooks/useTelemetry' // Import telemetry hook

interface DesktopEmailSubmitProps {
  resetState: () => void
  isV2: boolean
  variant: 'upload' | 'email'
}

interface EmailLookupResponse {
  email: string
  first_name: string
}

export default function EmailSubmitModal({
  resetState,
  isV2,
  variant = 'upload',
}: DesktopEmailSubmitProps): JSX.Element {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [emailError, setEmailError] = useState('')
  const [viewModalTracked, setViewModalTracked] = useState(false) // State to track if the modal view event has been tracked

  const {
    state: { checksum },
  } = useLeadActions()

  const [apiStatus, setApiStatus] = useState<
    'idle' | 'submitting' | 'success' | 'error'
  >('idle')

  const axios = useAxios({ withPartnerAuth: true })
  const { classes, cx } = useDesktopEmailSubmitStyles()
  const { classes: commonClasses } = useCommonStyles()
  const { hostname } = useLocation()
  const { handleComplianceModalOpen } = useComplianceNote()
  const { t } = useTranslation()
  const { defaultResumeProduct, brandName } = useSiteMetadata()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const submitButtonRef = useRef<HTMLButtonElement | null>(null)
  const { refetchSession, pt } = useUser()
  const { mutate } = useStatsEvent()
  const { email: userEmail } = useUser()
  const { sendDataToGTM } = useGTM()
  const { isDesktop } = useWidgetQueries()
  const telemetry = useBETelemetry() // Initialize backend telemetry
  const { DesktopTRef } = WidgetTRefs

  const labelPrefix = useMemo(() => {
    if (variant === 'email') return 'linkUploadSubmit'
    return isV2 ? 'emailSubmitV2' : 'emailSubmit'
  }, [isV2, variant])

  const UploadStatusComponent = isV2 ? ResumeUploadStatusV2 : ResumeUploadStatus

  const handleErrorModalClose = useCallback(() => {
    setApiStatus('idle')
  }, [])

  const handleSuccessModalClose = useCallback(() => {
    setApiStatus('idle')
    resetState()
  }, [resetState])

  const handleEmailLinkSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()

      // Trigger the click_get_review event immediately when the form is submitted
      telemetry.track({
        event: 'click_get_review',
        properties: {
          timestamp: Date.now() / 1000,
          entered_email: email,
          url: window.location.href,
        },
      })

      if (['loading', 'success'].includes(apiStatus)) return
      const data = Object.fromEntries(new FormData(e.currentTarget).entries()) as {
        email: string
      }

      if (!isEmail(data.email)) {
        setEmailError(t(`${WidgetTRefs.CommonTRef}.emailError`))

        // Track invalid email submission
        telemetry.track({
          event: 'click_get_review',
          properties: {
            timestamp: Date.now() / 1000,
            entered_email: data.email,
            error_text: 'Please enter a valid email address.',
            url: window.location.href,
          },
        })
        return
      }

      setApiStatus('submitting')

      axios
        .post<PartnerApiResponse>(Endpoints.LeadsEmail, data, {
          params: { partner_key: atob(pt ?? '') },
          validateStatus: (status) => status === 200 || status === 409,
        })
        .then((response) => {
          const {
            status: { code: statusCode },
          } = response.data
          if (statusCode === '200') {
            mutate(EventNames.frontendMobileEmailUpload)
          } else {
            mutate(EventNames.frontendMobileEmailUploadFailure)
          }
          setApiStatus('success')
        })
        .catch(() => {
          mutate(EventNames.frontendMobileEmailUploadFailure)
          setApiStatus('error')
        })
    },
    [apiStatus, axios, email, mutate, pt, telemetry, t]
  )

  const handleChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    (event) => {
      if (emailError) {
        setEmailError('')
      }

      setEmail(event.target.value)
    },
    [emailError]
  )

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()

      // Trigger the click_get_review event immediately when the form is submitted
      telemetry.track({
        event: 'click_get_review',
        properties: {
          timestamp: Date.now() / 1000,
          entered_email: email,
          url: window.location.href,
        },
      })

      if (apiStatus === 'submitting') {
        return
      }

      if (!isEmail(email)) {
        setEmailError(t(`${WidgetTRefs.CommonTRef}.emailError`))

        // Track invalid email submission
        telemetry.track({
          event: 'click_get_review',
          properties: {
            timestamp: Date.now() / 1000,
            entered_email: email,
            error_text: 'Please enter a valid email address.',
            url: window.location.href,
          },
        })

        return
      }

      if (isDesktop) {
        sendDataToGTM({
          event: UploadGTMEvents.ResumeUploadEmailSubmission,
        })
      }

      setApiStatus('submitting')

      try {
        const res = await axios.post<PartnerApiResponse>(Endpoints.LeadsEmail, {
          email,
          lead_capture_type_id: LeadsEmailPayload.LeadCaptureTypeId,
          document_digest: checksum,
          lead_source_id: LeadsEmailPayload.LeadSourceId,
          upload_source: hostname,
          tags: JSON.stringify({
            upload_source: hostname,
            referring_url: window.location.href,
          }),
        })
        const { data: response } = res
        const {
          status: { code: statusCode },
        } = response

        if (statusCode === '200' || statusCode === '202') {
          if (statusCode === '200') {
            mutate(EventNames.frontendEmailAddedToResumeUpload)
          } else {
            mutate(EventNames.frontendEmailAddedToResumeUploadFailure)
          }
          setApiStatus('success')
          const { data } = await refetchSession()

          if (isDesktop && !widgetSessionUtils.isUploadEventFired()) {
            if (data) {
              const {
                hashedEmail,
                userLeadAge,
                userLeadScore,
                partnerId,
                lead_auth_token: leadAuthToken,
              } = data

              sendDataToGTM({
                event: UploadGTMEvents.ResumeUpload,
                hashedEmail,
                userLeadAge,
                userLeadScore,
                partnerId,
                userId: leadAuthToken,
                blogCategory: 'All',
              })
            } else {
              sendDataToGTM({
                event: UploadGTMEvents.ResumeUpload,
              })
            }

            widgetSessionUtils.setUploadEventFired()
          }
        } else {
          mutate(EventNames.frontendEmailAddedToResumeUploadFailure)
          setApiStatus('error')
        }
      } catch (e: unknown) {
        if (defaultAxios.isAxiosError<Partial<PartnerApiResponse>>(e)) {
          const { response } = e

          if (response) {
            const {
              data: { status },
            } = response

            if (isUploadSucess(status?.code, status?.message)) {
              setApiStatus('success')
              const { data } = await refetchSession()

              if (isDesktop && !widgetSessionUtils.isUploadEventFired()) {
                if (data) {
                  const {
                    hashedEmail,
                    userLeadAge,
                    userLeadScore,
                    partnerId,
                    lead_auth_token: leadAuthToken,
                  } = data

                  sendDataToGTM({
                    event: UploadGTMEvents.ResumeUpload,
                    hashedEmail,
                    userLeadAge,
                    userLeadScore,
                    partnerId,
                    userId: leadAuthToken,
                    blogCategory: 'All',
                  })
                } else {
                  sendDataToGTM({
                    event: UploadGTMEvents.ResumeUpload,
                  })
                }

                widgetSessionUtils.setUploadEventFired()
              }

              return
            }
          }
          mutate(EventNames.frontendEmailAddedToResumeUploadFailure)
        }

        setApiStatus('error')
      }
    },
    [
      apiStatus,
      axios,
      checksum,
      email,
      hostname,
      isDesktop,
      mutate,
      refetchSession,
      sendDataToGTM,
      telemetry,
      t,
    ]
  )

  // Track the view_get_review_modal event once when the modal opens
  useEffect(() => {
    if (!viewModalTracked && apiStatus !== 'success') {
      telemetry.track({
        event: 'view_get_review_modal',
        properties: {
          timestamp: Date.now() / 1000,
          url: window.location.href,
        },
      })
      setViewModalTracked(true)
    }
  }, [apiStatus, telemetry, viewModalTracked])

  useEffect(() => {
    const raAbortController = new AbortController()
    const { signal } = raAbortController
    let timeoutId: ReturnType<typeof setTimeout>

    if (userEmail) {
      setEmail(userEmail)
      return
    }

    if (checksum) {
      async function getEmail() {
        setApiStatus('submitting')
        try {
          const {
            data: { email, first_name: firstName },
          } = await axios.get<EmailLookupResponse>(
            `api/v2/parse/${checksum}/email-lookup`,
            {
              signal,
              ...defaultRetryConfig,
            }
          )

          setEmail(email)
          setName(firstName)
          setApiStatus('idle')

          // Focus the submit button for immediate submission
          submitButtonRef.current?.focus()
        } catch {
          setApiStatus('idle')

          // Focus the email input if autofill fails
          inputRef.current?.focus()
        } finally {
          clearTimeout(timeoutId)
        }
      }

      getEmail()

      // Cancel the RA request after a certain timeout
      timeoutId = setTimeout(() => {
        setApiStatus('idle')
        raAbortController.abort()

        // Focus the email input if autofill fails
        inputRef.current?.focus()
      }, Timeouts.EmailLookup)
    }

    return () => {
      clearTimeout(timeoutId)
      raAbortController.abort()
    }
  }, [axios, checksum, userEmail])

  const isSubmitting = apiStatus === 'submitting'

  return (
    <>
      {isSubmitting && <BlueDotLoader />}

      <Dialog
        open={apiStatus !== 'success'}
        // open
        aria-labelledby="email-dialog-title"
        aria-describedby="email-dialog-desc"
        classes={{
          paper: classes.dialog,
          container: classes.dialogContainer,
        }}
      >
        {isV2 && (
          <IconButton
            className={classes.closeButton}
            onClick={handleSuccessModalClose}
            size="large"
          >
            <Close fontSize="small" />
          </IconButton>
        )}
        <DialogTitle id="email-dialog-title" className={classes.dialogTitle}>
          {name ? (
            <Typography variant="h2" className={classes.zeroMargin}>
              {t(`${DesktopTRef}.${labelPrefix}.titleWithName`, {
                name,
              })}
            </Typography>
          ) : (
            <Typography variant="h2" className={classes.zeroMargin}>
              {t(`${DesktopTRef}.${labelPrefix}.title`)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent
          className={cx(classes.dialogContent, commonClasses.hideScrollbar)}
        >
          <DialogContentText
            className={classes.dialogContentText}
            id="email-dialog-desc"
          >
            {t(`${DesktopTRef}.${labelPrefix}.body`, {
              defaultResumeProduct,
            })}
          </DialogContentText>
          <Box
            component="form"
            onSubmit={variant === 'upload' ? handleSubmit : handleEmailLinkSubmit}
            className={classes.form}
          >
            <TextField
              variant="standard"
              inputRef={inputRef}
              name="email"
              placeholder={t(`${DesktopTRef}.${labelPrefix}.placeholder`)}
              fullWidth
              type="text"
              InputProps={{
                className: classes.textField,
                disableUnderline: true,
                endAdornment: !isV2 && (
                  <Button
                    className={classes.reviewCta}
                    type="submit"
                    ref={submitButtonRef}
                  >
                    {t(`${DesktopTRef}.${labelPrefix}.ctaText`)}
                  </Button>
                ),
              }}
              inputProps={{
                className: cx({
                  [classes.nativeInput]: true,
                  [classes.nativeInputV2]: isV2,
                }),
              }}
              value={email}
              onChange={handleChange}
              error={!!emailError}
              helperText={emailError}
            />
            {isV2 && (
              <Button
                type="submit"
                endIcon={
                  isSubmitting && (
                    <CircularProgress size={24} />
                  )
                }
                disabled={isSubmitting}
                style={{ marginTop: '1.2em' }}
                className={classes.reviewCtaV2}
                ref={submitButtonRef}
              >
                {t(`${DesktopTRef}.${labelPrefix}.ctaText`)}
              </Button>
            )}
          </Box>

          <ComplianceNote>
            <Box className={classes.complianceNoticeContainer}>
              <LockIcon />
              <Typography
                component="span"
                variant="caption"
                className={classes.complianceNotice}
              >
                {t(`${DesktopTRef}.complianceNote.part1`, {
                  defaultResumeProduct,
                  brandName,
                })}
              </Typography>

              <ComplianceNote.TextLink onClick={handleComplianceModalOpen}>
                &nbsp;{t(`${WidgetTRefs.CommonTRef}.tos`)}
              </ComplianceNote.TextLink>

              <Typography
                component="span"
                variant="caption"
                className={classes.complianceNotice}
              >
                &nbsp;{t(`${DesktopTRef}.complianceNote.part2`)}
              </Typography>

              <ComplianceNote.TextLink onClick={handleComplianceModalOpen}>
                &nbsp;{t(`${WidgetTRefs.CommonTRef}.pp`)}
              </ComplianceNote.TextLink>

              <Typography
                component="span"
                variant="caption"
                className={classes.complianceNotice}
              >
                .
              </Typography>
            </Box>
          </ComplianceNote>
        </DialogContent>
      </Dialog>

      {/* Error modal */}
      <UploadStatusComponent
        open={apiStatus === 'error'}
        onClose={handleErrorModalClose}
        status={apiStatus}
      />

      {/* Success modal */}
      <UploadStatusComponent
        open={apiStatus === 'success'}
        onClose={handleSuccessModalClose}
        status={apiStatus}
      />
    </>
  )
}

const useDesktopEmailSubmitStyles = makeStyles()((theme) => ({
  dialogContainer: {
    '&:focus': {
      outline: 0,
    },
  },
  closeButton: {
    position: 'absolute',
    top: 0,
    right: 0,
    color: theme.palette.secondary.dark,
    '&:focus': {
      outline: 0,
    },
  },
  dialog: {
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    background: theme.palette.background.default,
    textAlign: 'left',
    maxWidth: 600,
    margin: '0.75rem',

    [theme.breakpoints.up('tablet')]: {
      padding: '2.25rem 1.5rem',
      gap: theme.spacing(3),
      textAlign: 'center',
    },

    [theme.breakpoints.up('sm')]: {
      padding: '1.5rem',
    },
  },
  dialogTitle: {
    padding: 0,
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
    gap: theme.spacing(1.5),

    [theme.breakpoints.up('tablet')]: {
      alignItems: 'center',
      gap: theme.spacing(3),
      padding: '0 2rem',
    },
  },
  dialogContentText: {
    margin: 0,
  },
  form: {
    alignSelf: 'stretch',
  },
  textField: {
    border: 'none',
    flexDirection: 'column',
    gap: '0.75rem',
    alignItems: 'flex-start',

    [theme.breakpoints.up('tablet')]: {
      alignItems: 'center',
      gap: 0,
      flexDirection: 'row',
      borderRadius: '6px',
      border: `1px solid ${theme.colors.gray['a200']}`,
    },
    '& > input': {
      flex: 1,
    },
  },
  nativeInput: {
    padding: '0.75rem 1rem',
    border: `1px solid ${theme.colors.gray['a200']}`,
    borderRadius: '6px',
    boxSizing: 'border-box',
    flexBasis: 'fit-content',

    [theme.breakpoints.up('tablet')]: {
      flexBasis: 'auto',
      border: 'none',
    },
  },
  nativeInputV2: {
    boxSizing: 'initial',
  },
  reviewCta: {
    width: 'fit-content',
    fontSize: '1rem',
    borderRadius: `${theme.spacing(0.75)}px`,

    [theme.breakpoints.up('tablet')]: {
      borderRadius: `0 6px 6px 0`,
    },
  },
  reviewCtaV2: {
    width: '100%',
    marginBlock: '1.75em',
    fontSize: '1rem',
    borderRadius: '6px',
  },
  complianceNoticeContainer: {
    width: '96%',

    '& svg': {
      width: 24,
      height: 24,
      position: 'relative',
      top: 7,
      right: 5,
    },
  },
  complianceNotice: {
    color: theme.palette.secondary.dark,
  },
  zeroMargin: {
    margin: 0,
    textTransform: 'none',
  },
}))
